// Roles y permisos (D99): que puede hacer cada rol en la Consola. Los roles son los de Twenty (dan el acceso a los
// datos); aqui se decide que pantallas y acciones tiene cada uno. Los grupos (buzonGrupo.miembros) siguen decidiendo
// que correo, casos, accesos y campanas ve cada persona.
// Lo guardado vive en el objeto permisoRol (una fila por rol); sin fila, cada rol usa sus permisos de fabrica, que
// reproducen lo que hacia antes de D99.

export type Permiso = {
  clave: string;
  nombre: string;
  ayuda: string;
  // El servidor y el motor lo exigen al Administrador (permiso WORKSPACE): en otro rol no funcionaria.
  soloAdmin?: boolean;
};
export type GrupoPermisos = { grupo: string; permisos: Permiso[] };

export const GRUPOS_PERMISOS: GrupoPermisos[] = [
  {
    grupo: 'Bandeja',
    permisos: [
      { clave: 'casos.sinGrupo', nombre: 'Ver casos sin grupo', ayuda: 'Casos que no son de ningún grupo del CRM' },
      { clave: 'casos.reasignar', nombre: 'Reasignar casos', ayuda: 'Asignar o quitar casos de otras personas' },
      { clave: 'conocimiento.borrar', nombre: 'Borrar artículos', ayuda: 'Eliminar artículos de Conocimiento' },
      { clave: 'accesos.papelera', nombre: 'Papelera de accesos', ayuda: 'Ver, restaurar e historial de la bóveda' },
    ],
  },
  {
    grupo: 'Clientes',
    permisos: [
      { clave: 'clientes.borrar', nombre: 'Borrar clientes', ayuda: 'Eliminar contactos y empresas' },
      { clave: 'clientes.exportar', nombre: 'Exportar a Excel', ayuda: 'Descargar listas de contactos y empresas' },
    ],
  },
  {
    grupo: 'Ventas',
    permisos: [
      { clave: 'oportunidades.ver', nombre: 'Oportunidades', ayuda: 'Ver y mover el embudo comercial' },
      { clave: 'proyectos.valores', nombre: 'Valores de proyectos', ayuda: 'Ver valor vendido, tarifa y valor consumido' },
      { clave: 'horas.equipo', nombre: 'Horas del equipo', ayuda: 'Ver las horas de todos' },
      { clave: 'horas.aprobar', nombre: 'Aprobar horas', ayuda: 'Aprobar o rechazar horas registradas' },
      { clave: 'bolsas.gestionar', nombre: 'Gestionar bolsas', ayuda: 'Crear bolsas de soporte y ajustar saldos' },
      { clave: 'licencias3cx.ver', nombre: 'Licencias 3CX', ayuda: 'Por vencer y renovaciones' },
    ],
  },
  {
    grupo: 'Marketing',
    permisos: [
      { clave: 'campanas.ver', nombre: 'Ver campañas', ayuda: 'Campañas de sus grupos y resultados' },
      { clave: 'campanas.crear', nombre: 'Crear campañas', ayuda: 'Armar listas, plantillas y borradores' },
      { clave: 'campanas.enviar', nombre: 'Enviar campañas', ayuda: 'Enviar, programar o detener un envío' },
    ],
  },
  {
    grupo: 'Reportes',
    permisos: [
      { clave: 'reportes.casos', nombre: 'Casos y áreas', ayuda: 'Tiempos de respuesta y volumen' },
      { clave: 'reportes.horas', nombre: 'Horas', ayuda: 'Horas por persona, proyecto y cliente' },
      { clave: 'reportes.bolsas', nombre: 'Bolsas', ayuda: 'Consumo y saldos de soporte' },
      { clave: 'reportes.correo', nombre: 'Correo', ayuda: 'Volumen y respuesta por buzón' },
      { clave: 'reportes.3cx', nombre: 'Llamadas 3CX', ayuda: 'Tableros de la central' },
      { clave: 'reportes.costos', nombre: 'Costos de llamadas', ayuda: 'Tarificador' },
      { clave: 'equipo.hoy', nombre: 'Equipo hoy', ayuda: 'Quién está disponible y en qué' },
    ],
  },
  {
    grupo: 'Llamadas',
    permisos: [
      { clave: 'llamadas.supervisar', nombre: 'Supervisar llamadas', ayuda: 'Escuchar, susurrar o entrar a una llamada' },
      { clave: 'llamadas.colas', nombre: 'Colas de otros', ayuda: 'Cambiar el estado de cola de otra persona' },
    ],
  },
  {
    grupo: 'Administración',
    permisos: [
      { clave: 'admin.usuarios', nombre: 'Usuarios y roles', ayuda: 'Invitar personas, cambiar roles y esta matriz', soloAdmin: true },
      { clave: 'admin.grupos', nombre: 'Grupos y buzones', ayuda: 'Crear grupos y decidir quién está en cada uno', soloAdmin: true },
      { clave: 'admin.plantillas', nombre: 'Plantillas', ayuda: 'Plantillas de correo, firmas y tipificaciones' },
      { clave: 'admin.vistas', nombre: 'Vistas de casos', ayuda: 'Vistas guardadas para el equipo' },
      { clave: 'admin.automatizaciones', nombre: 'Automatizaciones', ayuda: 'Reglas automáticas de casos y correo' },
      { clave: 'admin.integraciones', nombre: 'Integraciones', ayuda: 'Integración 3CX, tarifas y salud del correo', soloAdmin: true },
      { clave: 'admin.licencia', nombre: 'Licencia y espacio', ayuda: 'Plan, módulos y almacenamiento', soloAdmin: true },
    ],
  },
];

export const PERMISOS = GRUPOS_PERMISOS.flatMap((g) => g.permisos);
export const CLAVES = PERMISOS.map((p) => p.clave);
export type ClavePermiso = (typeof CLAVES)[number];
const SOLO_ADMIN = new Set(PERMISOS.filter((p) => p.soloAdmin).map((p) => p.clave));
export const esSoloAdmin = (clave: string) => SOLO_ADMIN.has(clave);

// Las claves que empiezan por alguno de estos prefijos ("reportes" = todos los reportes).
const de = (...prefijos: string[]) => CLAVES.filter((k) => prefijos.some((p) => k === p || k.startsWith(`${p}.`)));

// Roles de fabrica, por su etiqueta en Twenty. "Member" es el rol por defecto de Twenty: en pantalla, Agente de soporte.
export const ROL_ADMIN = 'Admin';
export const ROLES_FIJOS = ['Admin', 'Gerencia', 'Supervisor', 'Member', 'Ventas'] as const;
export const NOMBRE_ROL: Record<string, string> = { Admin: 'Administrador', Member: 'Agente de soporte' };
export const nombreRol = (etiqueta: string) => NOMBRE_ROL[etiqueta] ?? etiqueta;

export const PREDETERMINADOS: Record<string, string[]> = {
  Admin: CLAVES,
  Gerencia: de('casos.sinGrupo', 'clientes.exportar', 'oportunidades', 'proyectos', 'horas.equipo', 'licencias3cx', 'campanas.ver', 'reportes', 'equipo'),
  Supervisor: de('casos', 'conocimiento', 'clientes.exportar', 'oportunidades', 'horas', 'bolsas', 'licencias3cx', 'campanas', 'reportes', 'equipo', 'llamadas', 'admin.plantillas'),
  Member: de('clientes.exportar', 'oportunidades', 'campanas.ver'),
  Ventas: de('clientes.exportar', 'oportunidades', 'proyectos', 'bolsas', 'licencias3cx', 'campanas', 'reportes.bolsas'),
};

export type RolTwenty = { id: string; label: string };
export type FilaPermisos = { id?: string; rolId: string; name?: string; permisos: string[] | null };

/** Permisos de un rol: los guardados o, sin fila, los de fabrica (un rol nuevo sin fila arranca como Agente). */
export function permisosDeRol(rol: RolTwenty, filas: FilaPermisos[]): Set<string> {
  if (rol.label === ROL_ADMIN) return new Set(CLAVES);
  const fila = filas.find((f) => f.rolId === rol.id);
  const lista = fila ? (fila.permisos ?? []) : (PREDETERMINADOS[rol.label] ?? PREDETERMINADOS.Member);
  return new Set(lista.filter((k) => CLAVES.includes(k) && !SOLO_ADMIN.has(k)));
}

/** Lo que puede hacer una persona: la union de sus roles. El administrador (WORKSPACE) puede todo. */
export function permisosDePersona(o: { admin: boolean; roles: RolTwenty[]; filas: FilaPermisos[] }): Set<string> {
  if (o.admin) return new Set(CLAVES);
  const s = new Set<string>();
  for (const r of o.roles) for (const k of permisosDeRol(r, o.filas)) s.add(k);
  return s;
}

/** Cambios entre lo guardado y lo editado (un interruptor = un cambio). */
export function diferencias(antes: Record<string, Set<string>>, ahora: Record<string, Set<string>>) {
  let n = 0;
  for (const rolId of new Set([...Object.keys(antes), ...Object.keys(ahora)])) {
    const a = antes[rolId] ?? new Set<string>();
    const b = ahora[rolId] ?? new Set<string>();
    for (const k of CLAVES) if (a.has(k) !== b.has(k)) n++;
  }
  return n;
}

/** Interruptores que se pueden mover en un rol (los de solo administrador no). */
export const asignables = CLAVES.filter((k) => !SOLO_ADMIN.has(k));
